import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, History, Download, Save, Upload, Expand, FileText, Plus, Check, Pen, Loader2, Trash2 } from 'lucide-react';
import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import EditableField from '../../../components/ui/EditableField';
import { documentFieldsConfig, sectionConfigs, DocumentFieldConfig } from './fieldsConfig';
import './documentdetail.css';
import { useTheme } from '../../../contexts/ThemeContext';
import { Document, DocumentStatus, DocumentType, DataClassification } from '../documentlibrary/mockDocuments';
import { cleanDateAndTimeObject, renderDate, toLocalDateString } from '../../../../shared/renderDate';
import { getDocument, saveDocument, createDocument, deleteDocument, recoverDocument } from '../../../services/documentsService';
import { useTenancy } from '@/contexts/TenancyContext';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firebaseConfig, storageRef } from '@/js/external-js/config/main_config';
import { toast } from 'sonner';
import DocumentPreview from './DocumentPreview';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid"; // @ts-ignore
import { Timestamp } from 'firebase/firestore';
import { convertStatus } from '../documentlibrary';
import { getValidToken } from '@/js/external-js/variableModules/utils/getValidToken.util';
import { MCMMappingsCard } from '@/components/mcm/MCMMappingsCard';
import { MCMService } from '@/services/mcmService';

// Document history interface (matching the structure from documentlibrary)
interface DocumentHistoryEntry {
  changedDate?: any;
  changedBy?: string;
  comments?: string;
  oldComments?: string;
  fileName?: string;
  oldFileName?: string;
  nextReviewDate?: any;
  oldNextReviewDate?: any;
  lastReviewDate?: any;
  oldLastReviewDate?: any;
  docHash?: string;
  oldDocHash?: string;
  procRef?: string[];
  procRefs?: string[];
  oldProcRef?: string[];
  fieldChanges?: any[];
  // Additional fields from backend history tracking
  uploadedBy?: string;
  oldUploadedBy?: string;
  classification?: string;
  oldClassification?: string;
  documentType?: string;
  oldDocumentType?: string;
  businessUnit?: string;
  oldBusinessUnit?: string;
  status?: string;
  oldStatus?: string;
}

interface DocumentPageProps {
  documentID?: string;
  prefillData?: Partial<Document>;
  onCancel?: () => void;
  onSave?: (document: Document) => void;
  onBack?: () => void | undefined;
  onBackNavigation?: () => void;
  referer?: string;
  isReadOnly?: boolean;
}

const DocumentPage: React.FC<DocumentPageProps> = ({
  documentID: propDocumentID,
  prefillData = {},
  onCancel,
  onSave,
  onBack,
  onBackNavigation,
  referer,
  isReadOnly = false
}) => {
  const { activeTenancy, users, userIdToName } = useTenancy();
  const params = useParams();
  const navigate = useNavigate();
  const tenancyId = activeTenancy?.id || (window as any)?.companyID;

  // Use prop document ID if provided, otherwise get from URL
  const documentID = propDocumentID || params.documentID;
  const [isCreateMode, setIsCreateMode] = useState(!documentID);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [docFields, setDocFields] = useState<DocumentFieldConfig[]>([]);
  useEffect(() => {
    setLoading(true);
    if (!activeTenancy.id || activeTenancy.id === '' || activeTenancy.id === 'Loading...') return;
    onAuthStateChanged(auth, () => {
      documentFieldsConfig(users).then((config) => {
        setDocFields(config);
        setLoading(false);
      });
    });
  }, [activeTenancy.id]);
  let { id } = useParams<{ id: string }>();
  id = id || documentID;
  // Global editing state
  // If in read-only mode, never allow editing
  const [isEditing, setIsEditing] = useState(isReadOnly ? false : false);
  // Track which field is being edited
  const [activeField, setActiveField] = useState<string | null>(null);
  const theme = useTheme();
  const [document, setDocument] = useState<Document | undefined>(undefined);
  // Track edited values before they're saved
  const [editedValues, setEditedValues] = useState<Record<string, any>>({});
  const [showHistory, setShowHistory] = useState(false);
  const historyPanelRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [showConfluenceImportDialog, setShowConfluenceImportDialog] = useState(false);
  const [isImportingConfluenceDoc, setIsImportingConfluenceDoc] = useState(false);
  const [showReviewDatesDialog, setShowReviewDatesDialog] = useState(false);
  const [lastReviewDateValue, setLastReviewDateValue] = useState<Date>(new Date());
  const [nextReviewDateValue, setNextReviewDateValue] = useState<Date>(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date;
  });
  // Track the new filename separately without updating the document yet
  const [newFileName, setNewFileName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [customFields, setCustomFields] = useState<Array<{ name: string, value: string }>>([]);

  // Inline document title renaming state
  const [isRenamingTitle, setIsRenamingTitle] = useState(false);
  const [pendingDisplayName, setPendingDisplayName] = useState('');

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleTitleRenameSave = async () => {
    if (!document || !id || isCreateMode || isReadOnly) {
      setIsRenamingTitle(false);
      return;
    }

    const trimmed = pendingDisplayName.trim();
    const currentDisplayName = (document as any)?.displayName || '';

    if (trimmed === currentDisplayName) {
      setIsRenamingTitle(false);
      return;
    }

    try {
      toast.loading('Saving name...');

      const existingDoc = await getDocument(id as string);
      if (!existingDoc) {
        throw new Error('Could not find document to update');
      }

      (existingDoc as any).displayName = trimmed || '';
      (existingDoc as any).edit = true;
      (existingDoc as any).updateMethod = 'edit';

      const savedDoc = await saveDocument(existingDoc, null);

      setDocument(prev => ({
        ...(prev as any),
        displayName: trimmed || '',
        fileName: (savedDoc as any).fileName || (prev as any)?.fileName || '',
      } as any));

      toast.success('Name updated');
    } catch (error) {
      console.error('Error updating document name:', error);
      toast.error('Failed to update name');
    } finally {
      toast.dismiss();
      setIsRenamingTitle(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      // Create a proper ChangeEvent for the file input
      const event = {
        target: {
          files: [file]
        },
        // Add required properties for ChangeEvent
        currentTarget: { files: [file] },
        nativeEvent: e.nativeEvent,
        bubbles: true,
        cancelable: true,
        defaultPrevented: false,
        eventPhase: 3,
        isTrusted: true,
        preventDefault: () => { },
        stopPropagation: () => { },
        timeStamp: Date.now(),
        type: 'change'
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      handleFileUpload(event);
    }
  };

  const handleClickUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Define loadDocument function at the component level so it can be used anywhere
  const loadDocument = async () => {
    if (!activeTenancy.id || activeTenancy.id === '' || activeTenancy.id === 'Loading...') return;

    try {
      setLoading(true);

      // Check if we're in create mode
      if (id === 'new') {
        console.log('Creating new document');
        setIsCreateMode(true);
        // If creating a new document and not in read-only mode, automatically start in editing mode
        if (isCreateMode && !isReadOnly) {
          setIsEditing(true); // Automatically start in editing mode
        }
        // Initialize with a new empty document
        const newDoc = {
          docID: '', // Will be assigned by the system
          fileName: '',
          documentType: 'Policy', // Default
          status: 'Draft', // Default
          classification: 'Business Confidential', // Default
          uploadedBy: auth.currentUser?.uid, // Store only the user ID string here
          dateAdded: (new Date()),
          nextReviewDate: (new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)), // Default to one year from now
          comments: '',
          procRef: [],
        };

        // DED-1507: Check for prefill data from props or sessionStorage (for new tab creation from Risk Register)
        let effectivePrefillData = prefillData;
        if (Object.keys(prefillData).length === 0) {
          try {
            const storedPrefillData = sessionStorage.getItem('documentPrefillData');
            if (storedPrefillData) {
              effectivePrefillData = JSON.parse(storedPrefillData);
              sessionStorage.removeItem('documentPrefillData'); // Clear after reading
              console.log('[DocumentPage] Loaded prefill data from sessionStorage:', effectivePrefillData);
            }
          } catch (e) {
            console.warn('[DocumentPage] Failed to parse sessionStorage prefill data:', e);
          }
        }

        if (Object.keys(effectivePrefillData).length > 0) {
          for (const key in effectivePrefillData) {
            console.log('Prefilling document with key:', {
              key,
              value: effectivePrefillData[key]
            });
            if (effectivePrefillData.hasOwnProperty(key)) {
              newDoc[key] = effectivePrefillData[key];
            }
          }
        }

        setDocument(newDoc);
        setLoading(false);
        return;
      }

      // If we have a documentID, load the document
      if (id || documentID) {
        const doc = await getDocument(id || documentID);
        if (doc) {
          console.log('Document found:', doc);
          // DED-943: Task Evidence documents should show 'Published' status
          const docTypeLower = doc?.documentType?.toLowerCase();
          const isEvidenceType = docTypeLower === 'evidence' || docTypeLower === 'task evidence';
          setDocument({
            id: doc.docID ?? '',
            docID: doc.docID ?? '',
            status: isEvidenceType ? 'Published' : convertStatus(doc.status, cleanDateAndTimeObject(doc.nextReviewDate)) as DocumentStatus,
            documentType: (doc.documentType as DocumentType) ?? 'Policy',
            owner: doc.owner || doc.uploadedBy || '',
            description: doc.description ?? '',
            fileName: doc.fileName ?? '',
            displayName: (doc as any).displayName ?? undefined,
            nextReviewDate: doc.nextReviewDate ? (doc.nextReviewDate) : '',
            lastReviewDate: doc.lastReviewDate ? (doc.lastReviewDate) : '',
            dateAdded: doc.dateAdded ? (doc.dateAdded) : '',
            dateUploaded: doc.dateUploaded ? (doc.dateUploaded) : '',
            classification: (doc.classification as DataClassification) ?? 'Business Confidential',
            comments: doc.comments ?? '',
            componentId: doc.componentId ?? '',
            confluenceURL: doc.confluenceURL ?? '',
            docHash: doc.docHash ?? '',
            documentHistory: doc.documentHistory ?? {},
            procRef: doc.procRef ?? [],
            stickyDoc: doc.stickyDoc ?? false,
            uploadedBy: doc.uploadedBy ?? '',
            businessUnit: doc.businessUnit ?? '',
            documentCategory: doc.documentCategory ?? '',
            relatedControls: doc.relatedControls ?? [],
            linkedFromTaskId: (doc as any).linkedFromTaskId ?? '',
            // DED-943: Load linked task information for Task Evidence documents
            sourceTaskId: (doc as any).sourceTaskId ?? '',
            sourceTaskTitle: (doc as any).sourceTaskTitle ?? '',
            relatedTask: (doc as any).relatedTask ?? '',
          });
          setIsCreateMode(false);
        } else {
          toast.error('Document not found');
          navigate('/documentlibrary');
        }
      }

    } catch (error) {
      console.error('Error loading document:', error);
      toast.error('Failed to load document');
      if (onCancel) {
        onCancel();
      } else {
        navigate('/documentlibrary');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadDocument = async () => {
      if (!id && !documentID) {
        // If we have prefill data, use it
        console.log('Prefilling document with data:', {
          prefillData,
          documentID,
          id
        });
        if (Object.keys(prefillData).length > 0) {
          setDocument(prev => ({
            ...prev,
            ...prefillData,
            procRefs: prefillData.procRefs || []
          }));
          setLoading(false);
          return;
        }

        // If we have a documentID, load the document
        if (id || documentID) {
          const doc = await getDocument(id || documentID);
          if (doc) {
            setDocument(doc);
          } else if (onCancel) {
            onCancel();
          } else {
            toast.error('Document not found');
            navigate('/documentlibrary');
          }
        }
      }

    };

    loadDocument();
  }, [id, documentID, navigate, onCancel, prefillData]);

  useEffect(() => {
    if (!activeTenancy.id || activeTenancy.id === '' || activeTenancy.id === 'Loading...') return;
    loadDocument();
  }, [activeTenancy.id, id]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement> | { target: { files: FileList | null } }) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      setNewFile(selectedFile);

      // Store the new filename without updating the document yet
      setNewFileName(selectedFile.name);
    }
  };

  const handleDelete = async () => {
    if (!document?.docID) return;

    setIsDeleting(true);
    try {
      await deleteDocument(typeof document.docID === 'string' ? document.docID : document.docID.toString());
      toast.success('Document deleted successfully');
      navigate('/documentlibrary');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to delete document');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleConfluenceImport = async () => {
    if (!document?.confluenceURL) return;

    try {
      setIsImportingConfluenceDoc(true);

      // Extract page ID from Confluence URL (format: /spaces/SPACEKEY/pages/PAGE_ID/...)
      const url = new URL(document.confluenceURL);
      const pathSegments = url.pathname.split('/');
      const pagesIndex = pathSegments.indexOf('pages');
      const pageName = pathSegments[pagesIndex + 2];
      const pageId = pagesIndex !== -1 && pagesIndex + 1 < pathSegments.length
        ? pathSegments[pagesIndex + 1]
        : null;

      if (!pageId) {
        throw new Error('Could not extract page ID from Confluence URL. Make sure the URL contains "/pages/PAGE_ID/"');
      }

      // Call Confluence Export API using fetch to handle binary response
      const idToken = await getValidToken(auth);
      if (!idToken) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${import.meta.env.VITE_CONFLUENCE_API_URL}/export/${pageId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'customer-id': activeTenancy.id
          }
        }
      );

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to export Confluence page: ${error}`);
      }

      // Get the response as ArrayBuffer and convert to Blob
      const arrayBuffer = await response.arrayBuffer();
      const pdfBlob = new Blob([arrayBuffer], { type: 'application/pdf' });

      const fileName = `${pageName}.pdf`;

      // Create a File object from the blob
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Use the existing document replacement logic
      const event = {
        target: {
          files: [file]
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      handleFileUpload(event);
      // await handleSave();
      await new Promise(resolve => setTimeout(resolve, 3000));
      window.document.getElementById('save-changes-button')?.click();

      toast.success('Successfully imported Confluence document');
    } catch (error: any) {
      console.error('Error importing Confluence document:', error);
      toast.error(`Failed to import Confluence document: ${error.message}`);
    } finally {
      setIsImportingConfluenceDoc(false);
      setShowConfluenceImportDialog(false);
    }
  };

  const handleSave = async (e?: React.MouseEvent) => {
    // If this was triggered by a button click, prevent default
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      if (!activeTenancy.id) {
        console.error('No active tenancy');
        return;
      }

      setLoading(true);

      if (!document) {
        toast.error('No document to save');
        return;
      }

      // Debug: Log all document fields before save
      console.log('[DocumentDetail] All document fields before save:', Object.keys(document));
      console.log('[DocumentDetail] businessUnit field value:', document.businessUnit);
      console.log('[DocumentDetail] team field value:', (document as any).team);
      console.log('[DocumentDetail] Full document object:', document);

      // Check if this is a Confluence URL
      const hasConfluenceURL = !!document.confluenceURL;

      // Handle file name from uploaded file if present
      const documentFileName = newFileName || document.fileName;

      if (isCreateMode) {
        // We're creating a new document
        try {
          console.log('Creating new document...');

          // Prepare document data for creation - ensure all required properties are set
          const docToCreate = {
            ...document,
            fileName: documentFileName || (newFile ? newFile.name : 'Untitled Document'),
            docHash: '', // Will be generated by the system
            updateMethod: 'create',
            edit: false,
            // Explicitly set these required properties to ensure they're not undefined
            classification: document.classification || 'Business Confidential',
            documentType: document.documentType || document.type || 'Policy',
            status: document.status || 'Draft',
            uploadedBy: document.uploadedBy || auth.currentUser?.uid || '',
            dateAdded: document.dateAdded || (new Date()),
            nextReviewDate: document.nextReviewDate || (new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
            procRef: document.procRef || [],
            comments: document.comments || '',
            confluenceURL: document.confluenceURL || '',
            componentId: document.componentId || '',
            lastReviewDate: document.lastReviewDate || (new Date()),

          };

          // Use createDocument function
          const newDoc = await createDocument(docToCreate, newFile);

          // Show appropriate success message
          if (newFile) {
            toast.success('Document created and file uploaded successfully');
            setNewFile(null);
            setNewFileName(null);
          } else {
            toast.success('Document created successfully');
          }

          // If this is a Confluence URL, show the import dialog
          if (hasConfluenceURL) {
            setShowConfluenceImportDialog(true);
          } else {
            if (onSave && onSave instanceof Function) {
              onSave(newDoc);
            } else {
              // Navigate to the newly created document
              navigate(`/documentdetail/${newDoc.docID}`);
            }
          }
        } catch (createError) {
          console.error('Error creating document:', createError);
          toast.error('Failed to create document');
          throw createError;
        }
      } else {
        // We're editing an existing document
        // Mark the document as being edited and update filename if needed
        const docToSave = {
          ...document,
          edit: true,
          updateMethod: 'edit',
          // Apply the new filename if we have one
          fileName: documentFileName
        };

        console.log('Updating existing document:', docToSave);

        // Get the actual document instance from the service and update it
        const existingDoc = await getDocument(id as string);
        if (!existingDoc) {
          throw new Error('Could not find document to update');
        }

        // Copy properties from our edited document to the existing document
        Object.keys(docToSave).forEach(key => {
          if (key !== 'docID' && key !== 'id') { // Don't overwrite IDs
            (existingDoc as any)[key] = (docToSave as any)[key];
          }
        });

        // If we're replacing a file, set default dates in the document
        if (newFile) {
          // Set default dates
          const today = new Date();
          const nextYear = new Date();
          nextYear.setFullYear(today.getFullYear() + 1);

          // Update the document with default review dates
          setDocument(prev => ({
            ...prev,
            lastReviewDate: Timestamp.fromDate(today),
            nextReviewDate: Timestamp.fromDate(nextYear)
          }));
        }

        try {
          // Use saveDocument for both metadata updates and file uploads
          // This ensures we create only a single history event
          console.log('Saving document with saveDocument function');
          const savedDoc = await saveDocument(existingDoc, newFile);

          // Create the updated document object
          const updatedDoc: Document = {
            // Required fields with fallbacks
            id: savedDoc.id || document?.id || '',
            docID: savedDoc.docID || document?.docID || '',
            status: (savedDoc.status || document?.status || 'Draft') as DocumentStatus,
            documentType: (savedDoc.documentType || document?.documentType || 'Policy') as DocumentType,
            dateAdded: savedDoc.dateAdded || document?.dateAdded || new Date().toISOString(),
            owner: savedDoc.owner || document?.owner || '',
            description: savedDoc.description || document?.description || '',
            // Optional fields
            fileName: savedDoc.fileName || document?.fileName,
            confluenceURL: savedDoc.confluenceURL || document?.confluenceURL,
            // Preserve other existing document properties
            ...document,
            // Apply saved document properties, excluding undefined values
            ...Object.fromEntries(
              Object.entries(savedDoc)
                .filter(([_, v]) => v !== undefined)
            )
          };

          // Update the document state
          setDocument(updatedDoc);

          // Show appropriate success message based on whether a file was uploaded
          if (newFile) {
            toast.success('Document and file updated successfully');
            setNewFile(null); // Clear the selected file after successful upload
            setNewFileName(null); // Reset the new filename state
          } else {
            toast.success('Document updated successfully');
          }

          // If this is a Confluence URL, show the import dialog after a short delay
          // if (savedDoc.confluenceURL) {
          //   console.log('Confluence URL detected, showing import dialog');
          //   // Use a small timeout to ensure the state is updated before showing the dialog
          //   setTimeout(() => {
          //     setShowConfluenceImportDialog(true);
          //   }, 300);
          // }
        } catch (saveError) {
          console.error('Error saving document:', saveError);
          toast.error('Failed to save document changes');
          throw saveError; // Re-throw to be caught by the outer try-catch
        }

        // Reset editing state
        setIsEditing(false);
        setActiveField(null);

        // Update only the document history to prevent UI flash
        try {
          const updatedDoc = await getDocument(id || documentID);
          if (updatedDoc && updatedDoc.documentHistory) {
            setDocument(prevDoc => ({
              ...prevDoc,
              documentHistory: updatedDoc.documentHistory
            }));
          }
        } catch (historyError) {
          console.warn('Failed to update document history in UI:', historyError);
        }
      }
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    console.log('Downloading document...');
    if (!document) return;
    try {
      // Get the document ID for lookup
      const docId = document.componentId || document.docID || document.id;

      if (!docId) {
        toast.error('Document ID not found');
        return;
      }

      // Get the full document instance from the service
      getDocument(docId.toString()).then(docInstance => {

        if (!docInstance) {
          toast.error('Document not found');
          return;
        }

        // Now we can call download on the proper document instance
        docInstance.download().then(() => {
          toast.success('Document downloaded successfully');
        });
      })
    } catch (error) {
      console.error('Download error:', error);

      // Get a meaningful error message
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to download document. The file may not exist.';

      toast.error(errorMessage);
    }
  };

  const handleReplaceDocument = () => {
    setShowReplaceModal(true);
  };

  const handleFullScreen = () => {
    console.log('Opening document in full screen...');
    // The DocumentPreview component handles the full-screen functionality
    const previewElement = window.document.querySelector('.full-screen-trigger');
    if (previewElement) {
      (previewElement as HTMLElement).click();
    }
  };

  const addCustomField = () => {
    setCustomFields([...customFields, { name: 'New Field', value: '' }]);
  };

  const renderHistoryIcon = (iconName: string) => {
    switch (iconName) {
      case 'plus':
        return <Plus size={16} />;
      case 'pen':
        return <Pen size={16} />;
      case 'check':
        return <Check size={16} />;
      default:
        return <Pen size={16} />;
    }
  };

  useEffect(() => {
    if (showHistory && historyPanelRef.current) {
      historyPanelRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [showHistory]);

  console.log('Document:', document);

  // Controls linked to this document (selectedControls and relatedControls)
  const documentControlIds = useMemo<string[]>(() => {
    const toId = (x: any): string | null => {
      if (!x) return null;
      if (typeof x === 'string') return x.trim();
      if (typeof x === 'number') return String(x);
      if (typeof x === 'object') {
        return (x.controlID ?? x.value ?? x.id ?? '').toString().trim() || null;
      }
      return null;
    };
    const selSrc = Array.isArray((document as any)?.selectedControls) ? (document as any).selectedControls : [];
    const relSrc = Array.isArray((document as any)?.relatedControls) ? (document as any).relatedControls : [];
    const refSrc = Array.isArray((document as any)?.procRef) ? (document as any).procRef : [];
    const mapped = [...selSrc, ...relSrc, ...refSrc]
      .map(toId)
      .filter((v): v is string => !!v);
    return Array.from(new Set(mapped));
  }, [JSON.stringify((document as any)?.selectedControls || []), JSON.stringify((document as any)?.relatedControls || []), JSON.stringify((document as any)?.procRef || [])]);

  // When no control IDs are present on the document, discover them from MCM by scanning linkedComponents
  const [discoveredControlIds, setDiscoveredControlIds] = useState<string[]>([]);
  useEffect(() => {
    const discover = async () => {
      try {
        if (!tenancyId) return;
        const docId = String((document as any)?.docID || (document as any)?.id || '');
        const compId = String((document as any)?.componentId || '');
        if (!docId && !compId) return;

        const resp = await MCMService.getControls(false, tenancyId);
        const found = new Set<string>();
        const matchesDoc = (link: any) => {
          const domain = String(link?.domain || '').toLowerCase();
          if (domain !== 'document') return false;
          const ref = String(link?.ref ?? '').trim();
          const idStr = String(link?.id ?? '').trim();
          const matchRef = (candidate: string) => candidate !== '' && (ref === candidate);
          const matchId = (candidate: string) => {
            if (candidate === '') return false;
            const parts = idStr.split(':').filter(Boolean);
            // canonical form: internal:document:<ref>
            if (parts.length === 3 && parts[0] === 'internal' && parts[1] === 'document' && parts[2] === candidate) {
              return true;
            }
            // older internal:<ref> form (only accept when domain is Document)
            if (parts.length === 2 && parts[0] === 'internal' && parts[1] === candidate) {
              return true;
            }
            return false;
          };
          return (
            matchRef(docId) || matchRef(compId) || matchId(docId) || matchId(compId)
          );
        };

        // resp.Controls is an array of objects keyed by controlID with a 'standard' field
        for (const stdObj of resp.Controls as any[]) {
          for (const [key, val] of Object.entries(stdObj)) {
            if (key === 'standard') continue;
            const ctrl: any = val;
            const links = Array.isArray(ctrl?.linkedComponents) ? ctrl.linkedComponents : [];
            const legacyDocs: string[] = Array.isArray(ctrl?.supportingDocuments) ? ctrl.supportingDocuments : [];
            const legacyHit = legacyDocs.some((d) => {
              const s = String(d);
              return (s === docId || s === compId);
            });
            if (links.some(matchesDoc) || legacyHit) {
              found.add(String(key));
            }
          }
        }

        setDiscoveredControlIds(Array.from(found));
      } catch (e) {
        console.warn('[DocumentDetail] Failed to discover control IDs from MCM', e);
        setDiscoveredControlIds([]);
      }
    };

    // Always attempt augment discovery; it will be a no-op if nothing found
    discover();
  }, [tenancyId, (document as any)?.docID, (document as any)?.id, (document as any)?.componentId]);

  // Effective IDs: union of document-provided and discovered
  const effectiveControlIds = useMemo<string[]>(() => {
    const set = new Set<string>([...documentControlIds, ...discoveredControlIds]);
    return Array.from(set);
  }, [documentControlIds, discoveredControlIds]);

  // Helper to add MCM control to document's selectedControls
  const addMcmControlToDocument = useCallback(async (controlId: string) => {
    if (!document || !id || isCreateMode) return;

    const currentProcRef = Array.isArray((document as any)?.procRef)
      ? (document as any).procRef
      : [];

    if (currentProcRef.includes(controlId)) return;

    const updatedProcRef = [...currentProcRef, controlId];
    try {
      const existingDoc = await getDocument(id as string);
      if (!existingDoc) return;
      (existingDoc as any).procRef = updatedProcRef;
      (existingDoc as any).edit = true;
      (existingDoc as any).updateMethod = 'edit';
      await saveDocument(existingDoc, null);
      setDocument(prev => ({
        ...prev,
        procRef: updatedProcRef,
      } as any));
    } catch (e) {
      console.warn('[DocumentDetail] Failed to add control to selectedControls', e);
    }
  }, [document, id, isCreateMode]);

  // Render loading state or return to document library if document not found
  if (loading) {
    return (
      <div className="w-full max-w-[1100px] mx-auto py-8 flex justify-center items-center h-[60vh]">
        <div className="loading-overlay">
          <div className="spinner" />
          <p className="mt-4 text-center text-gray-600 dark:text-gray-400">Loading document...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="w-full max-w-[1100px] mx-auto py-8">
        {!isReadOnly && (
          <button
            className="back-button dark:!text-white/80 dark:!border-white/30"
            onClick={() => onBack ? onBack() : navigate('/documentlibrary')}
          >
            <ArrowLeft className="mr-2" /> Back to {referer ? referer : 'Document Library'}
          </button>
        )}
        <div className="glass-card p-8 text-center">
          <h2 className="text-2xl font-bold text-black dark:text-white mb-4">Document Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">The document you are looking for could not be found or may have been removed.</p>
          {!isReadOnly && (
            <button
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/80"
              onClick={() => onBack ? onBack() : navigate('/documentlibrary')}
            >
              Return to {referer ? referer : 'Document Library'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1100px] mx-auto py-8">

      {!isReadOnly && (
        <button
          className="back-button dark:!text-white/80 dark:!border-white/30"
          onClick={() => onBack ? onBack() : navigate('/documentlibrary')}
        >
          <ArrowLeft className="mr-2" /> Back to {referer ? referer : 'Document Library'}
        </button>
      )}

      <div className="glass-card">
        <div className="flex flex-row items-start justify-between mb-6 gap-4">
          <div className="flex-1 min-w-0">
            {isCreateMode ? (
              <h2 className="text-2xl font-bold text-black dark:text-white mb-1">Create New Document</h2>
            ) : (
              <>
                <div className="flex items-center gap-2 group" data-force-isms-override>
                  {isRenamingTitle && !isReadOnly ? (
                    <input
                      className="text-2xl font-bold text-black dark:text-white mb-1 bg-transparent border-b border-gray-300 focus:outline-none focus:border-primary"
                      autoFocus
                      value={pendingDisplayName}
                      onChange={(e) => setPendingDisplayName(e.target.value)}
                      onBlur={handleTitleRenameSave}
                      data-force-isms-override
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleTitleRenameSave();
                        }
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          setIsRenamingTitle(false);
                        }
                      }}
                    />
                  ) : (
                    <h2
                      className="text-2xl font-bold text-black dark:text-white mb-1 whitespace-normal break-words break-all"
                      style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                      data-force-isms-override
                    >
                      {(document as any)?.displayName || document.fileName || 'Untitled Document'}
                    </h2>
                  )}
                  {!isReadOnly && !isCreateMode && !isRenamingTitle && (
                    <button
                      type="button"
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-primary"
                      onClick={() => {
                        const effectiveName = (document as any)?.displayName || document.fileName || '';
                        setPendingDisplayName(effectiveName);
                        setIsRenamingTitle(true);
                      }}
                      aria-label="Rename document"
                    >
                      <FontAwesomeIcon icon={faPenToSquare} className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 classification-badge classification-${(document.classification || 'business-confidential').toLowerCase().replace(' ', '-')}`}>
                    {document.classification || 'Business Confidential'}
                  </span>
                </div>
              </>
            )}
          </div>
          <div className="button-group flex-shrink-0">
            {isCreateMode && !isReadOnly ? (
              <button
                className="btn btn-primary"
                onClick={handleSave}
              >
                <Save className="mr-2" /> Create Document
              </button>
            ) : (
              <>
                {/* Only show Delete button when not in read-only mode and not a Task Evidence document */}
                {/* DED-943: Task Evidence documents should not have a delete button */}
                {!isReadOnly && document?.documentType?.toLowerCase() !== 'task evidence' && (
                  <button
                    className="btn btn-outline text-red-600 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isDeleting}
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-2" />{isDeleting ? 'Deleting...' : 'Delete'}
                  </button>
                )}
                <button
                  className="btn btn-outline mr-2 dark:!text-white/80 dark:!border-white/30"
                  onClick={() => setShowHistory(prev => !prev)}
                >
                  <History className="mr-2" /> History
                </button>
                <button
                  className="btn btn-outline mr-2 dark:!text-white/80 dark:!border-white/30"
                  onClick={handleDownload}
                >
                  <Download className="mr-2" /> Download
                </button>
                {/* Only show Save Changes button when not in read-only mode */}
                {!isReadOnly && (
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    id="save-changes-button"
                  >
                    <Save className="mr-2" /> Save Changes
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Document Preview Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">
            {isCreateMode ? 'Upload Document' : 'Document Preview'}
          </h3>

          {/* Document Preview or Upload Area */}
          <div className="mb-4">
            {newFile ? (
              <div className="flex flex-col items-center justify-center bg-gray-100 dark:bg-neutral-800 rounded-lg py-8">
                <FileText size={48} className="text-primary mb-2" />
                <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">{newFile.name}</p>
                <p className="text-xs text-gray-500">{(newFile.size / 1024).toFixed(2)} KB • {newFile.type}</p>
              </div>
            ) : isCreateMode ? (
              // Upload area for new document
              <div
                className="bg-gray-100 dark:bg-neutral-800 rounded-lg"
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    // If handleFileUpload expects an event, adapt to accept dropped files
                    handleFileUpload({ target: { files: e.dataTransfer.files } } as React.ChangeEvent<HTMLInputElement>);
                  }
                }}
              >
                <label htmlFor="file-upload" className="w-full h-full flex flex-col items-center justify-center cursor-pointer py-8">
                  <Upload size={48} className="text-gray-400 dark:text-gray-500 mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">Click to upload or drag and drop</p>
                  <p className="text-xs text-gray-500">PDF, DOC, DOCX or other document formats</p>
                </label>
              </div>
            ) : (
              // Preview for existing document
              <div className="hidden full-screen-trigger" onClick={() => handleFullScreen()}></div>
            )}

            {/* Document Preview Component */}
            {!isCreateMode && document && (
              <DocumentPreview
                fileName={document.fileName || ''}
                filePath={`customers/${activeTenancy.id}/keyDocuments/otherDocs/uploaded/${document.docID}/${document.fileName}`}
                docId={document.docID?.toString() || ''}
                companyId={activeTenancy.id}
                onReplace={handleReplaceDocument}
                isEditMode={!isCreateMode}
                isReadOnly={isReadOnly}
              />
            )}

            {/* Hidden file input */}
            <input
              id="file-upload"
              type="file"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>

          {/* Document Metadata */}
          {/* {!isCreateMode && (
            <div className="flex flex-row gap-6 text-sm text-gray-700 dark:text-gray-300 mb-2">
              <span>Type: {document.fileType || document.documentType}</span>
              {document.fileSize && <span>Size: {document.fileSize}</span>}
              {document.dateAdded && <span>Created: {renderDate(document.dateAdded)}</span>}
            </div>
          )} */}

          {/* Action Buttons */}
          <div className="flex flex-row gap-3">
            {isCreateMode && newFile && (
              <button className="btn btn-outline" onClick={() => setNewFile(null)}>
                <Upload className="mr-2" /> Change File
              </button>
            )}
          </div>
        </div>

        {/* Render all sections and fields from config */}
        {Array.from(new Set(docFields.map(f => f.section))).map(section => (
          <div className="mb-8" key={section}>
            <h3 className="text-lg font-semibold mb-4">{section}</h3>
            <div
              className="grid gap-6"
              style={{
                gridTemplateColumns: `repeat(${sectionConfigs[section as string]?.itemsPerRow || 2}, 1fr)`
              }}
            >
              {docFields.filter(f => f.section === section).filter(field => {
                // Hide linkedFromTaskId field if it has no value
                if (field.key === 'linkedFromTaskId' && !document?.linkedFromTaskId) {
                  return false;
                }
                return true;
              }).map(field => (
                <div
                  key={field.key}
                  className={field.fullWidth ? 'col-span-full' : ''}
                  style={field.fullWidth ? { gridColumn: '1 / -1' } : {}}
                >
                  <EditableField
                    id={field.key}
                    key={field.key}
                    label={field.label}
                    value={(() => {
                      // Get the current value
                      const currentValue = field.key in editedValues ? editedValues[field.key] : document?.[field.key];

                      // For fields with object options like uploadedBy, we need to handle differently
                      if (field.key === 'uploadedBy' && field.options && field.options.length > 0 && typeof field.options[0] === 'object') {
                        // If we have a value, return that value, otherwise return the first option's value
                        return currentValue || (field.options[0] as any).value || '';
                      }

                      // For normal fields, return the value or the first option
                      return currentValue || field.options?.[0] || '';
                    })()}
                    type={field.type}
                    shouldHide={{ fields: ['nextReviewDate', 'lastReviewDate'], when: document?.documentType?.toLowerCase() === 'evidence' || document?.documentType?.toLowerCase() === 'task evidence' }}
                    options={field.options}
                    // Always disable editing if in read-only mode
                    disabled={isReadOnly || field.readOnly}
                    editing={!isReadOnly && activeField === field.key}
                    setEditing={(editing) => {
                      // Don't allow setting to editing mode if in read-only mode
                      if (isReadOnly) return;

                      if (editing) setActiveField(field.key);
                      else setActiveField(null);
                    }}
                    onSave={async (value) => {
                      try {
                        console.log('Saving field:', field.key, value);
                        // Update local state
                        if (field.key === 'nextReviewDate' || field.key === 'lastReviewDate') {
                          // Convert value to Date properly using our shared utility function
                          // Store as regular Date in local state, will convert to Timestamp when saving
                          value = cleanDateAndTimeObject(value) || new Date();
                        }

                        const isConfluenceUrlField = field.key === 'confluenceURL';

                        setEditedValues({ ...editedValues, [field.key]: value });
                        setDocument({ ...document, [field.key]: value });
                        setActiveField(null);

                        if (!isCreateMode) {
                          // Show loading indicator
                          toast.loading(`Saving ${field.label}...`);

                          if (!document) {
                            toast.error('No document to save');
                            return;
                          }

                          // Get the latest document to ensure we're working with fresh data
                          const existingDoc = await getDocument(id as string);
                          if (!existingDoc) {
                            throw new Error('Could not find document to update');
                          }

                          // Update only the specific field that changed
                          // Convert Date objects back to Timestamp for Firestore compatibility
                          let dbValue: any = value;
                          if (field.key === 'nextReviewDate' || field.key === 'lastReviewDate') {
                            // Ensure we're working with a Date object before converting to Timestamp
                            if (value instanceof Date) {
                              dbValue = Timestamp.fromDate(value);
                            } else if (typeof value === 'string') {
                              dbValue = Timestamp.fromDate(new Date(value));
                            } else {
                              // Fallback for unexpected types
                              dbValue = Timestamp.fromDate(new Date());
                            }
                          }
                          existingDoc[field.key] = dbValue;
                          existingDoc.edit = true;
                          existingDoc.updateMethod = 'edit';

                          // Save the changes to the database
                          await saveDocument(existingDoc, null);

                          // Stop loading indicator
                          toast.dismiss();

                          // If this is a Confluence URL field and it has a value, show the import dialog
                          if (isConfluenceUrlField && value) {
                            console.log('Confluence URL saved, showing import dialog');
                            // Small delay to ensure the state is updated
                            setTimeout(() => {
                              setShowConfluenceImportDialog(true);
                            }, 300);
                          }
                        } else if (isConfluenceUrlField && value) {
                          // For new documents, just show the dialog immediately
                          console.log('New document with Confluence URL, showing import dialog');
                          setTimeout(() => {
                            setShowConfluenceImportDialog(true);
                          }, 300);
                        }

                        // Show success message
                        toast.success(`${field.label} updated successfully`);
                        
                        // Update only the document history to prevent UI flash
                        try {
                          const updatedDoc = await getDocument(id || documentID);
                          if (updatedDoc && updatedDoc.documentHistory) {
                            setDocument(prevDoc => ({
                              ...prevDoc,
                              documentHistory: updatedDoc.documentHistory
                            }));
                          }
                        } catch (historyError) {
                          console.warn('Failed to update document history in UI:', historyError);
                        }
                      } catch (error) {
                        console.error(`Error saving ${field.label}:`, error);
                        toast.error(`Failed to save ${field.label}`);
                      }
                    }}
                    readOnly={field.readOnly}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

      </div>

      {/* DED-943: Linked Task Card for Task Evidence documents */}
      {document?.documentType?.toLowerCase() === 'task evidence' && (document as any)?.sourceTaskId && (
        <div className="bg-white/85 dark:bg-neutral-900/80 backdrop-blur-md rounded-xl border border-black/10 dark:border-white/10 shadow-lg p-6 mb-6 mt-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-blue-500">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
            </svg>
            Linked Task
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            This document was uploaded as evidence during task performance.
          </p>
          <div className="grid gap-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  {(document as any)?.sourceTaskTitle || 'Unknown Task'}
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Task ID: {(document as any)?.sourceTaskId}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-blue-600 border-blue-300 hover:bg-blue-100 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/50"
                onClick={() => navigate('/compliance-calendar')}
              >
                View in Calendar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MCM Mappings Card */}
      <MCMMappingsCard
        tenancyId={tenancyId}
        isReadOnly={isReadOnly}
        controlIds={effectiveControlIds}
        defaultEntityLink={{
          id: `internal:document:${String((document as any)?.docID || (document as any)?.id || '')}`,
          ref: String((document as any)?.docID || (document as any)?.id || ''),
          title: (document as any)?.fileName || (document as any)?.componentId || ((document as any)?.docID || ''),
          domain: 'Document',
          relation: 'supports',
        }}
        onEnsureControlLinked={async (controlId) => {
          await addMcmControlToDocument(controlId);
        }}
      />

      {/* History Panel (toggleable) */}
      {showHistory && !isCreateMode && (
        <div className="bg-white/85 dark:bg-neutral-900/80 backdrop-blur-md rounded-xl border border-black/10 dark:border-white/10 shadow-lg p-6 mb-6 overflow-hidden" ref={historyPanelRef}>
          <h3 className="text-lg font-semibold mb-4">Change History</h3>
          <div className="flex flex-col gap-4">
            {document?.documentHistory ? (
              Object.entries(document.documentHistory)
                .filter(([key]) => key !== 'historyCount')
                .sort((a, b) => {
                  // Sort by timestamp, latest first
                  const dateA = a[1] as DocumentHistoryEntry;
                  const dateB = b[1] as DocumentHistoryEntry;

                  const timeA = dateA.changedDate?.__time__ ||
                    (dateA.changedDate?.seconds ? dateA.changedDate.seconds * 1000 : 0);
                  const timeB = dateB.changedDate?.__time__ ||
                    (dateB.changedDate?.seconds ? dateB.changedDate.seconds * 1000 : 0);

                  return timeB - timeA; // Latest first
                })
                .map(([key, entry], index) => {
                  const historyEntry = entry as DocumentHistoryEntry;
                  if (!historyEntry.changedDate) return null;

                  // Format the date
                  const changedDate = historyEntry.changedDate as any;
                  // Convert various date formats to a consistent display using our utility function
                  let dateObj;
                  if (changedDate.__time__) {
                    dateObj = new Date(changedDate.__time__);
                  } else if (changedDate.seconds) {
                    dateObj = new Date(changedDate.seconds * 1000);
                  } else if (typeof changedDate === 'string') {
                    dateObj = new Date(changedDate);
                  }

                  // Format date and time consistently - show full date and time for history entries
                  const formattedDate = dateObj
                    ? new Intl.DateTimeFormat('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }).format(dateObj)
                    : 'Unknown date';

                  // Get user name who made the change
                  const changedByUserId = historyEntry.changedBy;
                  const changedByName = changedByUserId ? (userIdToName[changedByUserId] || 'Unknown user') : 'Unknown user';

                  // Determine history entry type and icon
                  let title = 'Document Updated';
                  let icon = 'pen';
                  let details = '';

                  // Helper to format field value for display
                  const formatFieldValue = (value: any): string => {
                    if (value == null) return '';
                    if (typeof value === 'object') {
                      if (value.__time__ || value.seconds || value.toDate) {
                        // Handle date objects
                        let dateObj;
                        if (value.__time__) {
                          dateObj = new Date(value.__time__);
                        } else if (value.seconds) {
                          dateObj = new Date(value.seconds * 1000);
                        } else if (value.toDate) {
                          dateObj = value.toDate();
                        }
                        if (dateObj && !isNaN(dateObj.getTime())) {
                          return dateObj.toLocaleDateString();
                        }
                      }
                      try { return JSON.stringify(value); } catch { return '[object]'; }
                    }
                    return String(value);
                  };

                  // Build specific change details
                  const changes: string[] = [];
                  
                  // Check for file changes
                  if (historyEntry.fileName && !historyEntry.oldFileName) {
                    title = 'Document Created';
                    icon = 'plus';
                    changes.push(`File uploaded: ${historyEntry.fileName}`);
                  } else if (historyEntry.fileName && historyEntry.oldFileName) {
                    title = 'File Replaced';
                    icon = 'upload';
                    changes.push(`File changed from ${historyEntry.oldFileName} to ${historyEntry.fileName}`);
                  }

                  // Check for comment changes
                  if (historyEntry.comments !== historyEntry.oldComments) {
                    if (historyEntry.comments && !historyEntry.oldComments) {
                      changes.push(`Comments added: "${historyEntry.comments}"`);
                    } else if (!historyEntry.comments && historyEntry.oldComments) {
                      changes.push('Comments removed');
                    } else {
                      changes.push(`Comments updated: "${historyEntry.comments}"`);
                    }
                  }

                  // Check for review date changes
                  if (historyEntry.lastReviewDate !== historyEntry.oldLastReviewDate) {
                    if (historyEntry.lastReviewDate && !historyEntry.oldLastReviewDate) {
                      changes.push(`Last review date set to ${formatFieldValue(historyEntry.lastReviewDate)}`);
                    } else if (!historyEntry.lastReviewDate && historyEntry.oldLastReviewDate) {
                      changes.push('Last review date cleared');
                    } else {
                      changes.push(`Last review date changed from ${formatFieldValue(historyEntry.oldLastReviewDate)} to ${formatFieldValue(historyEntry.lastReviewDate)}`);
                    }
                  }

                  // Check for next review date changes
                  if (historyEntry.nextReviewDate !== historyEntry.oldNextReviewDate) {
                    if (historyEntry.nextReviewDate && !historyEntry.oldNextReviewDate) {
                      changes.push(`Next review date set to ${formatFieldValue(historyEntry.nextReviewDate)}`);
                    } else if (!historyEntry.nextReviewDate && historyEntry.oldNextReviewDate) {
                      changes.push('Next review date cleared');
                    } else {
                      changes.push(`Next review date changed from ${formatFieldValue(historyEntry.oldNextReviewDate)} to ${formatFieldValue(historyEntry.nextReviewDate)}`);
                    }
                  }

                  // Check for hash changes (indicates file content change)
                  if (historyEntry.docHash !== historyEntry.oldDocHash) {
                    changes.push('File content updated');
                  }

                  // Check for process reference changes
                  if (JSON.stringify(historyEntry.procRef) !== JSON.stringify(historyEntry.oldProcRef)) {
                    const oldRefs = historyEntry.oldProcRef || [];
                    const newRefs = historyEntry.procRef || [];
                    
                    // Calculate what was added and what was removed
                    const added = newRefs.filter(ref => !oldRefs.includes(ref));
                    const removed = oldRefs.filter(ref => !newRefs.includes(ref));
                    
                    // Show specific changes
                    if (added.length > 0 && removed.length === 0) {
                      changes.push(`Process references added: ${added.join(', ')}`);
                    } else if (removed.length > 0 && added.length === 0) {
                      changes.push(`Process references removed: ${removed.join(', ')}`);
                    } else if (added.length > 0 && removed.length > 0) {
                      changes.push(`Process references added: ${added.join(', ')}`);
                      changes.push(`Process references removed: ${removed.join(', ')}`);
                    }
                  }

                  // Check for owner/uploadedBy changes
                  if (historyEntry.uploadedBy !== historyEntry.oldUploadedBy) {
                    const oldOwnerName = historyEntry.oldUploadedBy ? (userIdToName[historyEntry.oldUploadedBy] || 'Unknown user') : 'None';
                    const newOwnerName = historyEntry.uploadedBy ? (userIdToName[historyEntry.uploadedBy] || 'Unknown user') : 'None';
                    if (oldOwnerName && newOwnerName) {
                      changes.push(`Owner changed from "${oldOwnerName}" to "${newOwnerName}"`);
                    } else if (newOwnerName && !oldOwnerName) {
                      changes.push(`Owner set to "${newOwnerName}"`);
                    } else if (!newOwnerName && oldOwnerName) {
                      changes.push('Owner cleared');
                    }
                  }

                  // Check for business unit changes
                  if (historyEntry.businessUnit !== historyEntry.oldBusinessUnit) {
                    const oldBusinessUnit = historyEntry.oldBusinessUnit || 'None';
                    const newBusinessUnit = historyEntry.businessUnit || 'None';
                    if (oldBusinessUnit && newBusinessUnit && oldBusinessUnit !== newBusinessUnit) {
                      changes.push(`Business Unit changed from "${oldBusinessUnit}" to "${newBusinessUnit}"`);
                    } else if (newBusinessUnit && (!oldBusinessUnit || oldBusinessUnit === 'None')) {
                      changes.push(`Business Unit set to "${newBusinessUnit}"`);
                    } else if ((!newBusinessUnit || newBusinessUnit === 'None') && oldBusinessUnit && oldBusinessUnit !== 'None') {
                      changes.push('Business Unit cleared');
                    }
                  }

                  // Check for classification changes
                  if (historyEntry.classification !== historyEntry.oldClassification) {
                    const oldValue = formatFieldValue(historyEntry.oldClassification);
                    const newValue = formatFieldValue(historyEntry.classification);
                    if (oldValue && newValue) {
                      changes.push(`Data Classification changed from "${oldValue}" to "${newValue}"`);
                    } else if (newValue && !oldValue) {
                      changes.push(`Data Classification set to "${newValue}"`);
                    } else if (!newValue && oldValue) {
                      changes.push('Data Classification cleared');
                    }
                  }

                  // Check for document type changes
                  if (historyEntry.documentType !== historyEntry.oldDocumentType) {
                    const oldValue = formatFieldValue(historyEntry.oldDocumentType);
                    const newValue = formatFieldValue(historyEntry.documentType);
                    if (oldValue && newValue) {
                      changes.push(`Document Type changed from "${oldValue}" to "${newValue}"`);
                    } else if (newValue && !oldValue) {
                      changes.push(`Document Type set to "${newValue}"`);
                    } else if (!newValue && oldValue) {
                      changes.push('Document Type cleared');
                    }
                  }

                  // Check for status changes
                  if (historyEntry.status !== historyEntry.oldStatus) {
                    const oldValue = formatFieldValue(historyEntry.oldStatus);
                    const newValue = formatFieldValue(historyEntry.status);
                    if (oldValue && newValue) {
                      changes.push(`Status changed from "${oldValue}" to "${newValue}"`);
                    } else if (newValue && !oldValue) {
                      changes.push(`Status set to "${newValue}"`);
                    } else if (!newValue && oldValue) {
                      changes.push('Status cleared');
                    }
                  }

                  // Check for field changes array
                  if (historyEntry.fieldChanges && Array.isArray(historyEntry.fieldChanges)) {
                    historyEntry.fieldChanges.forEach((fieldChange: any) => {
                      if (fieldChange.fieldName && fieldChange.oldValue !== fieldChange.newValue) {
                        const fieldName = fieldChange.fieldName;
                        const oldValue = formatFieldValue(fieldChange.oldValue);
                        const newValue = formatFieldValue(fieldChange.newValue);
                        
                        if (oldValue && newValue) {
                          changes.push(`${fieldName} changed from "${oldValue}" to "${newValue}"`);
                        } else if (newValue && !oldValue) {
                          changes.push(`${fieldName} set to "${newValue}"`);
                        } else if (!newValue && oldValue) {
                          changes.push(`${fieldName} cleared`);
                        }
                      }
                    });
                  }

                  // Use the changes array for details, or fall back to generic message
                  if (changes.length > 0) {
                    details = changes.join('; ');
                  } else {
                    details = 'Document metadata updated';
                  }

                  return (
                    <div className="flex flex-row items-center gap-4 p-3 bg-gray-50 dark:bg-neutral-800 rounded-lg" key={key}>
                      <div className="flex-shrink-0 text-blue-500 dark:text-blue-300">
                        {renderHistoryIcon(icon)}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-800 dark:text-gray-200">{title}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 line-break-words break-words min-w-0">{details}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">By: {changedByName}</div>
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">{formattedDate}</div>
                    </div>
                  );
                })
            ) : (
              <div className="text-center text-gray-500 py-6">
                <p>No history available for this document</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Replace Document Dialog */}
      <Dialog
        open={showReplaceModal}
        onOpenChange={(open) => {
          setShowReplaceModal(open);
          if (!open) {
            // Reset states when dialog is closed
            setNewFile(null);
            setNewFileName(null);
          }
        }}
      >
        <DialogContent className="bg-white dark:bg-secondary">
          <DialogHeader>
            <DialogTitle>Replace Document</DialogTitle>
            <DialogDescription>
              Select a new file to replace the current document.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="flex flex-col items-center justify-center w-full space-y-4">
              <div
                className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg transition-colors ${isDragging
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
                  }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center justify-center p-6 text-center">
                  <div className={`p-3 mb-3 rounded-full ${isDragging
                      ? 'bg-blue-100 dark:bg-blue-900/30'
                      : 'bg-gray-100 dark:bg-gray-600/30'
                    }`}>
                    <Upload className={`w-6 h-6 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                  </div>
                  {newFile ? (
                    <div className="max-w-full">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{newFile.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {(newFile.size / 1024 / 1024).toFixed(2)} MB • {newFile.type}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Drag and drop your file here
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        PDF, DOC, DOCX, or other document formats (max 50MB)
                      </p>
                    </div>
                  )}
                </div>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClickUpload();
                }}
                className="flex items-center gap-2"
              >
                <Upload className="h-4 w-4" />
                Select File
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReplaceModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                console.log('Replacing document with:', newFile?.name);
                // Show the review dates dialog
                if (newFile) {
                  // Reset date values to defaults
                  const today = new Date();
                  const nextYear = new Date();
                  nextYear.setFullYear(today.getFullYear() + 1);

                  setLastReviewDateValue(today);
                  setNextReviewDateValue(nextYear);

                  // Also update the document instance
                  if (document) {
                    document.lastReviewDate = Timestamp.fromDate(today);
                    document.nextReviewDate = Timestamp.fromDate(nextYear);
                  }

                  setShowReviewDatesDialog(true);
                }
                setShowReplaceModal(false);
              }}
              disabled={!newFile}
            >
              Upload & Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white dark:bg-secondary">
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              The document will be moved to the trash and can be recovered within 30 days.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Document
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confluence Import Dialog */}
      <Dialog open={showConfluenceImportDialog} onOpenChange={setShowConfluenceImportDialog}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle>Import Confluence Document</DialogTitle>
            <DialogDescription>
              Would you like to import this Confluence page as a PDF and attach it to this document?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfluenceImportDialog(false)}
              disabled={isImportingConfluenceDoc}
            >
              No, keep current document
            </Button>
            <Button
              type="button"
              onClick={handleConfluenceImport}
              disabled={isImportingConfluenceDoc}
            >
              {isImportingConfluenceDoc ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                'Yes, import PDF'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dates Dialog */}
      <Dialog open={showReviewDatesDialog} onOpenChange={(open) => {
        setShowReviewDatesDialog(open);
        // If closing the dialog, continue with the save process
        if (!open) {
          setTimeout(() => {
            handleSave();
          }, 0);
        }
      }}>
        <DialogContent className="sm:max-w-md bg-card">
          <DialogHeader>
            <DialogTitle>Update Review Dates</DialogTitle>
            <DialogDescription>
              Set the last and next review dates for this document.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              You're replacing the document file. Do you want to update the review dates?
            </p>
            <div className="space-y-4">
              <div>
                <DatePicker
                  label="Last Review Date"
                  date={lastReviewDateValue}
                  setDate={(date) => {
                    if (date) {
                      setLastReviewDateValue(date);
                      // Update the document field directly
                      setDocument(prev => ({
                        ...prev,
                        lastReviewDate: Timestamp.fromDate(date)
                      }));

                      // Also update the existing document instance to ensure save works
                      if (document) {
                        document.lastReviewDate = Timestamp.fromDate(date);
                      }
                    }
                  }}
                  fullWidth
                />
              </div>
              <div>
                <DatePicker
                  label="Next Review Date"
                  date={nextReviewDateValue}
                  setDate={(date) => {
                    if (date) {
                      setNextReviewDateValue(date);
                      // Update the document field directly
                      setDocument(prev => ({
                        ...prev,
                        nextReviewDate: Timestamp.fromDate(date)
                      }));

                      // Also update the existing document instance to ensure save works
                      if (document) {
                        document.nextReviewDate = Timestamp.fromDate(date);
                      }
                    }
                  }}
                  fullWidth
                />
              </div>
            </div>
          </div>
          <DialogFooter className="sm:justify-start">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowReviewDatesDialog(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                setShowReviewDatesDialog(false);
                // When dialog closes with date updates, trigger the save process
                setTimeout(() => {
                  handleSave();
                }, 0);
              }}
            >
              Update Dates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <button
        className="back-button dark:!text-white/80 dark:!border-white/30"
        onClick={() => onBackNavigation ? onBackNavigation() : navigate('/documentlibrary')}
      >
        <ArrowLeft className="mr-2" /> Back to {referer ? referer : 'Document Library'}
      </button>
    </div>
  );
};

export default DocumentPage;
